import { useCallback, useEffect, useRef, useState } from "react";
import type { Material, Quote, QuoteParams } from "@fprint/shared";
import { Button, buttonClasses, useBrand } from "@fprint/ui";
import { Link } from "react-router-dom";

import { PriceDisclosure } from "../components/PriceDisclosure";
import { QuoteForm } from "../components/QuoteForm";
import { QuoteResult } from "../components/QuoteResult";
import { QuoteStatusNotice } from "../components/QuoteStatusNotice";
import { StlDropzone } from "../components/StlDropzone";
import { StlPreview, type StlPreviewHandle } from "../components/StlPreview";
import { CustomerHeader } from "../components/CustomerHeader";
import { SubmitForReview } from "../components/SubmitForReview";
import { useAuth } from "../lib/auth";
import { createQuote, fetchMaterials, presignUpload, uploadStl } from "../lib/api";
import { usePublicConfig } from "../lib/publicConfig";
import {
  clearResumeQuoteId,
  markResumeAfterAuth,
  persistResumeQuoteId,
  readSessionResumeQuoteId,
} from "../lib/resumeQuote";
import { useQuotePolling } from "../lib/useQuotePolling";

type UploadStatus =
  | { phase: "idle" }
  | { phase: "uploading" }
  | { phase: "uploaded"; key: string }
  | { phase: "error"; error: string };

/** Safari CORS-blocked fetch → "Load failed"; Chrome → "Failed to fetch". */
const OPAQUE_UPLOAD_FETCH_MESSAGES = new Set(["load failed", "failed to fetch"]);

export function humanizeQuoteSubmitError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);
  // Keep the real TypeError in the console; the UI string is mapped.
  console.error("Quote submit failed:", err);
  if (OPAQUE_UPLOAD_FETCH_MESSAGES.has(raw.trim().toLowerCase())) {
    return "Couldn't upload the model. Please retry.";
  }
  return raw;
}

export function QuotePage() {
  const brand = useBrand();
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const tokenGetter = useCallback(() => getToken().catch(() => null), [getToken]);
  const publicConfig = usePublicConfig();

  const [file, setFile] = useState<File | null>(null);
  const [upload, setUpload] = useState<UploadStatus>({ phase: "idle" });
  const [materials, setMaterials] = useState<Material[]>([]);
  const [materialsError, setMaterialsError] = useState<string | null>(null);
  const [quoteId, setQuoteId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // When the user changes scale after a quote is ready, the old quote no
  // longer matches the geometry. Keep it around (greyed out) with an
  // explicit "get a new quote" banner instead of silently vanishing.
  const [staleQuote, setStaleQuote] = useState<Quote | null>(null);
  const poll = useQuotePolling(quoteId, tokenGetter);
  const resumeQuoteId = quoteId ?? readSessionResumeQuoteId();
  const guestSignInFrom = resumeQuoteId ? `/order/${resumeQuoteId}` : "/quote";

  // Scale + live dimensions (mm). Scale is applied client-side before the quote upload.
  const [scale, setScale] = useState(1.0);
  const [modelDims, setModelDims] = useState<{ x: number; y: number; z: number; max: number } | null>(null);
  const previewRef = useRef<StlPreviewHandle>(null);
  const formColRef = useRef<HTMLDivElement>(null);

  // UX-8: give the failed/error states a clear recovery path — scroll the
  // customer to the still-present quote form instead of leaving a dead-end
  // message they have to discover the form under.
  const scrollToForm = useCallback(() => {
    formColRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  useEffect(() => {
    fetchMaterials()
      .then(setMaterials)
      .catch((err: Error) => setMaterialsError(err.message));
  }, []);

  async function handleFile(f: File): Promise<void> {
    setFile(f);
    setUpload({ phase: "idle" }); // upload is now deferred until "Get quote"
    setQuoteId(null);
    setStaleQuote(null);
    setScale(1.0);
    setModelDims(null);
    clearResumeQuoteId();
  }

  async function handleQuoteSubmit(params: QuoteParams): Promise<void> {
    if (!file) return;
    setSubmitting(true);
    setUpload({ phase: "uploading" });
    setQuoteId(null);
    setStaleQuote(null);

    try {
      // Always upload the original STL. The chosen scale_factor is sent in params
      // and applied server-side during slicing (via PrusaSlicer --scale or stub math).
      // This keeps the original design file intact while still letting scale affect
      // material usage, time, and pricing calculations.
      const presign = await presignUpload(file.name, tokenGetter);
      await uploadStl(presign, file);
      setUpload({ phase: "uploaded", key: presign.key });

      const created = await createQuote(
        {
          stl_s3_key: presign.key,
          params: { ...params, scale_factor: scale },
        },
        tokenGetter,
      );
      persistResumeQuoteId(created.id);
      setQuoteId(created.id);
    } catch (err) {
      setUpload({
        phase: "error",
        error: humanizeQuoteSubmitError(err),
      });
    } finally {
      setSubmitting(false);
    }
  }

  function reset(): void {
    setFile(null);
    setUpload({ phase: "idle" });
    setQuoteId(null);
    setStaleQuote(null);
    setScale(1.0);
    setModelDims(null);
    clearResumeQuoteId();
  }

  // Changing scale after a quote exists requires a fresh quote (different
  // geometry). Keep the last ready quote visible-but-stale so the price
  // doesn't just vanish without explanation.
  function handleScaleChange(next: number) {
    const clamped = Math.max(0.25, Math.min(5, next));
    setScale(clamped);
    if (quoteId) {
      if (poll.phase === "ready") setStaleQuote(poll.quote);
      setQuoteId(null);
      setUpload({ phase: "idle" });
    }
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-4xl flex-col gap-8 px-6 py-12">
      <CustomerHeader
        variant="brand"
        subtitle={brand.copy["hero.subtitle"]}
        headerTestId="quote-header"
      />

      {publicConfig?.custom_work_enabled && (
        <p
          className="text-center text-sm text-muted-fg"
          data-testid="custom-work-entry"
        >
          Need design help, painting or something unusual?{" "}
          <Link to="/custom-work" className="underline">
            Request custom work
          </Link>
          .
        </p>
      )}

      {materialsError && (
        <p className="rounded-md bg-warning/10 px-4 py-3 text-sm text-warning-fg" role="alert">
          Failed to load materials: {materialsError}
        </p>
      )}

      {!file && <StlDropzone onFile={handleFile} />}

      {file && (
        <div className="grid gap-8 md:grid-cols-2">
          <div className="flex flex-col gap-3">
            <StlPreview
              ref={previewRef}
              file={file}
              scale={scale}
              onDimensionsChange={setModelDims}
            />

            {/* Live size + scale controls.
               The chosen scale is sent to the backend and applied during slicing
               (PrusaSlicer --scale or equivalent in stub) so material, time, and price
               correctly reflect the final size the customer wants. */}
            <div className="rounded-md border border-border bg-bg p-3 text-sm">
              <div className="mb-2 flex items-center justify-between">
                <span className="font-medium">Scale</span>
                <span className="tabular-nums font-mono text-muted-fg">{scale.toFixed(2)}×</span>
              </div>

              <input
                type="range"
                min={0.25}
                max={5}
                step={0.05}
                value={scale}
                aria-label="Scale"
                onChange={(e) => handleScaleChange(parseFloat(e.target.value))}
                className="w-full accent-primary"
                data-testid="scale-slider"
              />

              <div className="mt-2 flex flex-wrap gap-1.5">
                {[0.5, 1, 1.5, 2, 3].map((s) => (
                  <Button
                    key={s}
                    variant={Math.abs(scale - s) < 0.01 ? "primary" : "ghost"}
                    size="sm"
                    onClick={() => handleScaleChange(s)}
                    className="h-7 px-2 text-xs"
                  >
                    {s}×
                  </Button>
                ))}
                <input
                  type="number"
                  min={0.25}
                  max={5}
                  step={0.1}
                  value={scale}
                  onChange={(e) => handleScaleChange(parseFloat(e.target.value) || 1)}
                  className="ml-auto h-7 w-16 rounded border border-border bg-bg px-1 text-right text-xs tabular-nums"
                  data-testid="scale-input"
                />
              </div>

              {modelDims && (
                <div className="mt-2 text-xs text-muted-fg" data-testid="model-dims">
                  Size at {scale.toFixed(2)}×:{" "}
                  <span className="font-mono text-fg">
                    {modelDims.x.toFixed(1)} × {modelDims.y.toFixed(1)} × {modelDims.z.toFixed(1)} mm
                  </span>
                  <span className="ml-1 text-[10px]">(native {(modelDims.x / scale).toFixed(1)} mm max)</span>
                </div>
              )}

            </div>

            <div className="flex items-center justify-between text-sm text-muted-fg">
              <span data-testid="stl-filename">{file.name}</span>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => previewRef.current?.resetView()}
                  disabled={submitting}
                >
                  Reset view
                </Button>
                <Button variant="ghost" size="sm" onClick={reset} disabled={submitting}>
                  Replace
                </Button>
              </div>
            </div>

            {upload.phase === "uploading" && (
              <p className="text-sm text-muted-fg" data-testid="upload-status">
                Uploading (scaled) model…
              </p>
            )}
            {upload.phase === "error" && (
              <p className="text-sm text-danger-fg" role="alert">
                {upload.error}
              </p>
            )}

            {!isSignedIn && (
              <p className="text-xs text-muted-fg">
                Guest mode —{" "}
                <Link
                  to={`/sign-in?from=${encodeURIComponent(guestSignInFrom)}`}
                  className="underline"
                  // Carry the resume target: once a quote exists, sign-in
                  // should land back on /order/:quoteId (the quote lives
                  // server-side + sessionStorage), not on "/" where the
                  // context is lost. `?from=` survives an OTP refresh;
                  // location.state does not.
                  state={{ from: guestSignInFrom }}
                  onClick={() => {
                    const id = quoteId ?? readSessionResumeQuoteId();
                    if (id) {
                      persistResumeQuoteId(id);
                      markResumeAfterAuth();
                    }
                  }}
                >
                  sign in
                </Link>{" "}
                to place orders.
              </p>
            )}
          </div>

          <div ref={formColRef} className="flex flex-col gap-6">
            {/*
              QuoteForm initialises its `materialId` / `colorId` state from
              `materials[0]` on first mount and never re-derives. Mounting
              it before the fetch resolves wedges the form: state holds ""
              forever, the Colour <select> stays empty, and submits silent-
              no-op via the `if (!materialId || !colorId) return` guard.
              Gate the mount on materials being present so the initialiser
              always sees real data.
            */}
            {materials.length > 0 ? (
              <QuoteForm
                materials={materials}
                disabled={
                  submitting ||
                  !file ||
                  poll.phase === "polling"
                }
                onSubmit={handleQuoteSubmit}
              />
            ) : (
              !materialsError && (
                <p
                  className="text-sm text-muted-fg"
                  data-testid="materials-loading"
                >
                  Loading materials…
                </p>
              )
            )}

            {poll.phase === "polling" && (
              <p
                className="flex items-center gap-2 text-sm text-muted-fg"
                data-testid="quote-polling"
              >
                <span
                  aria-hidden="true"
                  className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-muted-fg border-t-transparent"
                />
                Slicing your model — this usually takes up to a minute.
              </p>
            )}
            {poll.phase === "timeout" && (
              <div
                className="flex flex-col gap-2 rounded-md border border-warning bg-warning/10 p-3 text-sm text-warning-fg"
                role="status"
                data-testid="quote-timeout"
              >
                <p>
                  Slicing is taking longer than usual. It may still finish —
                  you can keep waiting without re-uploading your file.
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  className="self-start"
                  onClick={poll.keepWaiting}
                  data-testid="quote-keep-waiting"
                >
                  Keep waiting
                </Button>
              </div>
            )}
            {poll.phase === "failed" && (
              <div
                className="flex flex-col gap-2 rounded-md border border-danger bg-danger/10 p-3 text-sm text-danger-fg"
                role="alert"
                data-testid="quote-failed"
              >
                <p>Quote failed: {poll.quote.error ?? "unknown error"}</p>
                <Button
                  variant="secondary"
                  size="sm"
                  className="self-start"
                  onClick={scrollToForm}
                  data-testid="quote-failed-retry"
                >
                  Get a new quote
                </Button>
              </div>
            )}
            {poll.phase === "error" && (
              <div
                className="flex flex-col gap-2 rounded-md border border-danger bg-danger/10 p-3 text-sm text-danger-fg"
                role="alert"
                data-testid="quote-error"
              >
                <p>{poll.error}</p>
                <Button
                  variant="secondary"
                  size="sm"
                  className="self-start"
                  onClick={scrollToForm}
                  data-testid="quote-error-retry"
                >
                  Get a new quote
                </Button>
              </div>
            )}
            {poll.phase === "ready" && (
              <>
                <QuoteResult quote={poll.quote} />
                {poll.quote.result?.price_cents != null && (
                  <PriceDisclosure totalCents={poll.quote.result.price_cents} />
                )}
                <Link
                  to={`/order/${poll.quote.id}`}
                  className={buttonClasses({ size: "lg", className: "self-start" })}
                  data-testid="place-order-cta"
                >
                  {brand.copy["cta.order"]}
                </Link>
              </>
            )}
            {/* Multistage terminal states: an `estimated` quote shows the
                machine estimate + submit-for-review (never a pay CTA);
                in_review / needs_manual_quote / declined get status panels. */}
            {poll.phase === "settled" &&
              (poll.quote.status === "estimated" ? (
                <>
                  <QuoteResult quote={poll.quote} estimate />
                  <p
                    role="status"
                    data-testid="quote-estimated-notice"
                    className="rounded-md border border-warning bg-warning/10 px-4 py-3 text-sm text-warning-fg"
                  >
                    This is a machine estimate — subject to review. Submit it
                    and our operator will email you the final quote before any
                    payment.
                  </p>
                  <SubmitForReview
                    quote={poll.quote}
                    getToken={tokenGetter}
                    signedIn={Boolean(isLoaded && isSignedIn)}
                  />
                </>
              ) : (
                <QuoteStatusNotice quote={poll.quote} />
              ))}
            {/* Stale quote after a scale change: keep it visible but greyed,
                with an explicit explanation instead of a silent vanish. */}
            {staleQuote?.result && !quoteId && poll.phase === "idle" && (
              <>
                <div
                  className="rounded-md border border-warning bg-warning/10 px-4 py-3 text-sm text-warning-fg"
                  role="status"
                  data-testid="quote-stale-banner"
                >
                  Size changed — this quote no longer matches. Press{" "}
                  <strong>{brand.copy["cta.quote"]}</strong> to get a new price
                  at {scale.toFixed(2)}×.
                </div>
                <div
                  aria-hidden="true"
                  className="pointer-events-none opacity-50 grayscale"
                  data-testid="quote-stale"
                >
                  <QuoteResult quote={staleQuote} />
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </main>
  );
}
